import { useLocale } from './useLocale';
import {
  normalizeCurrencySpacing,
  numberFormats,
} from 'dashboard/i18n/formats';

/**
 * Composable for number formatting with i18n locale support
 * Provides methods to format numbers in compact and full display formats
 */
export function useNumberFormatter() {
  const { resolvedLocale } = useLocale();

  /**
   * Formats numbers for display with clean, minimal formatting
   * - Up to 1,000: show exact number (e.g., 999)
   * - 1,000 to 999,999: show as "Xk" for exact thousands or "Xk+" for remainder (e.g., 1000 → "1k", 1500 → "1k+")
   * - 1,000,000+: show in millions with 1 decimal place (e.g., 1,234,000 → "1.2M")
   *
   * Uses browser-native Intl.NumberFormat with proper i18n locale support
   *
   * @param {number} num - The number to format
   * @returns {string} Formatted number string
   */
  const formatCompactNumber = num => {
    if (typeof num !== 'number' || Number.isNaN(num)) {
      return '0';
    }

    // For numbers between -1000 and 1000 (exclusive), show exact number with locale formatting
    if (Math.abs(num) < 1000) {
      return new Intl.NumberFormat(resolvedLocale.value).format(num);
    }

    // For numbers with absolute value above 1,000,000, show in millions with 1 decimal place
    if (Math.abs(num) >= 1000000) {
      const millions = num / 1000000;
      return new Intl.NumberFormat(resolvedLocale.value, {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1,
        minimumFractionDigits: millions % 1 === 0 ? 0 : 1,
      }).format(num);
    }

    // For numbers with absolute value between 1,000 and 1,000,000, show as "Xk" or "Xk+" using floor value
    // For negative numbers, we want to floor towards zero (truncate), not towards negative infinity
    const thousands = num >= 0 ? Math.floor(num / 1000) : Math.ceil(num / 1000);
    const remainder = Math.abs(num) % 1000;
    const suffix = remainder === 0 ? 'k' : 'k+';
    return `${new Intl.NumberFormat(resolvedLocale.value).format(thousands)}${suffix}`;
  };

  /**
   * Format a number for full display with locale-specific formatting
   * @param {number} num - The number to format
   * @returns {string} Formatted number string with full precision and locale formatting (e.g., 1,234,567)
   */
  const formatFullNumber = num => {
    if (typeof num !== 'number' || Number.isNaN(num)) {
      return '0';
    }

    return new Intl.NumberFormat(resolvedLocale.value).format(num);
  };

  /**
   * Formats a value as currency (BRL by default) using pt-BR conventions and
   * replaces the non-breaking space (U+00A0) after the symbol with a normal
   * space to avoid layout/copy-paste issues (see reference doc 03 §1.4).
   * e.g. 1234.56 → "R$ 1.234,56".
   *
   * @param {number} value - The amount to format.
   * @param {string} [currency='BRL'] - ISO 4217 currency code.
   * @returns {string} The formatted currency string (empty for invalid input).
   */
  const formatCurrency = (value, currency = 'BRL', options = {}) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '';
    }

    return normalizeCurrencySpacing(
      new Intl.NumberFormat('pt-BR', {
        ...numberFormats.pt_BR.currency,
        ...options,
        currency,
      }).format(value)
    );
  };

  return {
    formatCompactNumber,
    formatFullNumber,
    formatCurrency,
  };
}
